/**
 * My calendar — green appreciation dots, red flags, amber off-site days.
 * Route: /calendar  (wireframe 1b)
 * @file src/web/features/calendar/pages/index.tsx
 *
 * @llm-rule WHEN: Someone wants to see their month, or a supervisor opens a report's month
 * @llm-rule NOTE: Dots come from /api/remark/calendar, which merges remarks with
 *   off-site attendance so one call fills the whole grid.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Skeleton,
  Textarea,
  toast,
} from '@bloomneo/uikit';
import {
  ArrowLeft,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  Lock,
  PartyPopper,
  Users,
  Video,
} from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { PRODUCT } from '../../../shared/brand';
import type { FlagCode } from '../../../shared/flags';
import { Panel, PersonAvatar } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { formatClock, formatDuration, ROLE_LABEL, type DotKind, type OrgRole } from '../../../shared/domain';
import { EventDialog, type CalendarEvent } from '../components/EventDialog';
import {
  CountPill,
  FlagTriangle,
  HATCH,
  HolidaySwatch,
  OffsiteDiamond,
  PresentDot,
} from '../components/DayMarkers';

interface DayDots {
  day: string;
  dots: DotKind[];
}

interface Holiday {
  id: string;
  name: string;
  kind: 'NATIONAL' | 'FESTIVAL' | 'OPTIONAL';
  day: string;
}

interface AttendanceDay {
  id: string;
  day: string;
  clockInAt: string;
  clockOutAt: string | null;
  kind: string;
  approvalStatus: string;
  locationName: string | null;
  minutes: number;
  signOffNote: string | null;
}

interface RemarkAuthor {
  id: string;
  name: string | null;
  orgRole: string;
  title: string | null;
}

interface Remark {
  id: string;
  type: 'APPRECIATION' | 'FLAG';
  body: string;
  day: string;
  status: string;
  acknowledgedAt: string | null;
  createdAt: string;
  author: RemarkAuthor;
  /**
   * Set on every engine-raised flag. Its presence is what distinguishes an
   * automatic flag from one a person wrote — see the attribution below.
   */
  code: FlagCode | null;
}

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Sunday-start JS weekday → Monday-start grid column. */
function mondayIndex(date: Date): number {
  return (date.getUTCDay() + 6) % 7;
}

function CalendarPage() {
  const { user } = useAuth();
  const { request } = useAppRequest();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // A supervisor can open someone else's month with ?subjectId=…
  const subjectId = searchParams.get('subjectId') || user?.id || '';
  const isSelf = subjectId === user?.id;

  const now = new Date();
  /** Automatic flags are attributed to the company, so its name is needed here. */
  const [companyName, setCompanyName] = useState<string | null>(null);

  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);

  /** Whose month this is. Null while loading, or when it is your own. */
  const [subject, setSubject] = useState<{
    id: string;
    name: string | null;
    orgRole: OrgRole;
    title: string | null;
    initials: string | null;
  } | null>(null);
  const [dots, setDots] = useState<DayDots[]>([]);
  const [attendance, setAttendance] = useState<AttendanceDay[]>([]);
  const [remarks, setRemarks] = useState<Remark[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [weeklyOffDays, setWeeklyOffDays] = useState<number[]>([0]);
  const [eventOpen, setEventOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    /** EVENT reaches the whole company; REMINDER is private. */
    kind: 'EVENT' as 'EVENT' | 'REMINDER',
    title: '',
    startTime: '10:00',
    endTime: '',
    meetingLink: '',
    notes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);

    const [dotsResult, attendanceResult, remarksResult, eventsResult, holidayResult] =
      await Promise.all([
      request<{ dots: DayDots[]; subject: typeof subject }>(
        `/api/remark/calendar?subjectId=${subjectId}&year=${year}&month=${month}`,
      ),
      // Attendance detail is only available for yourself; a supervisor sees
      // the dots and the remarks, not the minute-by-minute record.
      isSelf
        ? request<{ days: AttendanceDay[] }>(`/api/attendance/calendar?year=${year}&month=${month}`)
        : Promise.resolve({ ok: true as const, data: { days: [] as AttendanceDay[] } }),
      request<{ remarks: Remark[] }>(
        `/api/remark/subject/${subjectId}?year=${year}&month=${month}`,
      ),
      request<{ events: CalendarEvent[] }>(`/api/event?year=${year}&month=${month}`),
      request<{ holidays: Holiday[]; weeklyOffDays: number[] }>(
        `/api/holiday?year=${year}&month=${month}`,
      ),
    ]);

    if (dotsResult.ok) {
      setDots(dotsResult.data.dots);
      setSubject(dotsResult.data.subject ?? null);
    } else {
      toast.error(dotsResult.error);
    }

    if (attendanceResult.ok) setAttendance(attendanceResult.data.days);
    if (remarksResult.ok) setRemarks(remarksResult.data.remarks);
    if (eventsResult.ok) setEvents(eventsResult.data.events);
    if (holidayResult.ok) {
      setHolidays(holidayResult.data.holidays);
      setWeeklyOffDays(holidayResult.data.weeklyOffDays);
    }

    setLoading(false);
  }, [request, subjectId, year, month, isSelf]);

  useEffect(() => {
    void request<{ organisation: { name: string } | null }>('/api/organisation/mine').then((r) => {
      if (r.ok && r.data.organisation) setCompanyName(r.data.organisation.name);
    });
  }, [request]);

  useEffect(() => {
    if (subjectId) void load();
  }, [load, subjectId]);

  const dotsByDay = useMemo(() => new Map(dots.map((d) => [d.day, d.dots])), [dots]);
  const attendanceByDay = useMemo(
    () => new Map(attendance.map((a) => [a.day, a])),
    [attendance],
  );

  const holidayByDay = useMemo(
    () => new Map(holidays.map((h) => [h.day, h])),
    [holidays],
  );

  /** Sunday (or whatever the company sets) is a non-working day. */
  const isWeeklyOff = useCallback(
    (iso: string) => weeklyOffDays.includes(new Date(`${iso}T00:00:00Z`).getUTCDay()),
    [weeklyOffDays],
  );

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const list = map.get(event.day) ?? [];
      list.push(event);
      map.set(event.day, list);
    }
    return map;
  }, [events]);

  async function addEvent() {
    if (!selected || !eventForm.title.trim()) return;

    const result = await request('/api/event', {
      method: 'POST',
      body: { ...eventForm, day: selected, endTime: eventForm.endTime || undefined },
    });
    if (!result.ok) return toast.error(result.error);

    toast.success(
      eventForm.kind === 'REMINDER' ? 'Reminder added — only you can see it' : 'Event added',
    );
    // Keep the kind: someone adding reminders usually adds several.
    setEventForm({
      kind: eventForm.kind,
      title: '',
      startTime: '10:00',
      endTime: '',
      meetingLink: '',
      notes: '',
    });
    setEventOpen(false);
    void load();
  }

  async function removeEvent(id: string) {
    const result = await request(`/api/event/${id}`, { method: 'DELETE' });
    if (!result.ok) return toast.error(result.error);
    toast.success('Event removed');
    void load();
  }

  /** Local YYYY-MM-DD, so "today" is the viewer's today, not UTC's. */
  const todayKey = useMemo(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
  }, []);

  const grid = useMemo(() => {
    const first = new Date(Date.UTC(year, month - 1, 1));
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const lead = mondayIndex(first);

    const cells: Array<{ key: string; date: string | null; n: number | null; outside?: boolean }> =
      [];

    // The design shows the neighbouring month's days rather than blank holes —
    // it keeps every week a full row, so the grid reads as a continuous month.
    const prevDays = new Date(Date.UTC(year, month - 1, 0)).getUTCDate();
    for (let i = lead; i > 0; i--) {
      cells.push({ key: `lead-${i}`, date: null, n: prevDays - i + 1, outside: true });
    }

    for (let n = 1; n <= daysInMonth; n++) {
      const date = `${year}-${String(month).padStart(2, '0')}-${String(n).padStart(2, '0')}`;
      cells.push({ key: date, date, n });
    }

    // Fill the last row out to seven.
    const trail = (7 - (cells.length % 7)) % 7;
    for (let i = 1; i <= trail; i++) {
      cells.push({ key: `trail-${i}`, date: null, n: i, outside: true });
    }

    return cells;
  }, [year, month]);

  function step(delta: number) {
    const next = new Date(Date.UTC(year, month - 1 + delta, 1));
    setYear(next.getUTCFullYear());
    setMonth(next.getUTCMonth() + 1);
    setSelected(null);
  }

  const selectedRemarks = selected ? remarks.filter((r) => r.day.startsWith(selected)) : [];
  const selectedAttendance = selected ? attendanceByDay.get(selected) : undefined;
  /** Everything worth showing for the open day — drives the "· 2 entries" header. */
  const selectedCount =
    selectedRemarks.length +
    (selectedAttendance ? 1 : 0) +
    (selected ? (eventsByDay.get(selected) ?? []).length : 0);

  const counts = useMemo(
    () => ({
      flags: remarks.filter((r) => r.type === 'FLAG').length,
      offSite: attendance.filter((a) => a.kind === 'OFF_SITE').length,
    }),
    [remarks, attendance],
  );



  const monthLabel = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <AppShell wide>
      <div className="mx-auto max-w-2xl">
        <Panel padded={false} className="overflow-hidden">
          {/* Month header */}
          {/*
            Whose calendar this is, stated plainly. A Company Admin opening a employee
            from People previously got "Viewing another person's record", which
            named nobody — with several tabs open there was no way to tell one
            employee's month from another's.
          */}
          {!isSelf && subject && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule bg-paper-sunk px-5 py-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <PersonAvatar person={subject} size={30} />
                <div className="min-w-0">
                  <div className="truncate text-[13.5px] font-semibold text-ink">
                    {subject.name}’s calendar
                  </div>
                  <div className="truncate text-[11.5px] text-ink-faint">
                    {subject.title || ROLE_LABEL[subject.orgRole]}
                  </div>
                </div>
              </div>

              <Button
                size="sm"
                variant="outline"
                className="h-8 flex-none gap-1.5 rounded-full px-3 text-[12px]"
                onClick={() => navigate('/calendar')}
              >
                <ArrowLeft className="size-3.5" />
                My calendar
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between border-b border-rule px-5 py-3.5">
            <div>
              <h1 className="text-[17px] font-semibold tracking-tight text-ink">{monthLabel}</h1>
              {!isSelf && subject && (
                <p className="text-[11.5px] text-ink-faint">{subject.name}</p>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" aria-label="Previous month" onClick={() => step(-1)}>
                <ChevronLeft className="size-4" />
              </Button>
              <Button variant="ghost" size="sm" aria-label="Next month" onClick={() => step(1)}>
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>

          <div className="p-5">
            {loading ? (
              <Skeleton className="h-64 w-full rounded-xl" />
            ) : (
              <>
                {/* Weekday header — Monday first, matching the design. */}
                <div className="mb-2 grid grid-cols-7 gap-1 text-center">
                  {WEEKDAYS.map((d, i) => (
                    <div key={`${d}-${i}`} className="label-mono text-ink-ghost">
                      {d}
                    </div>
                  ))}
                </div>

                {/* Day grid */}
                <div className="grid grid-cols-7 gap-1">
                  {grid.map((cell) => {
                    // Neighbouring month: shown, muted, and not selectable.
                    if (!cell.date) {
                      return (
                        <div
                          key={cell.key}
                          aria-hidden
                          className="flex h-[52px] flex-col items-center rounded-[9px] border border-dashed border-rule pt-1.5 text-[13px] text-ink-ghost/60"
                        >
                          {cell.n}
                        </div>
                      );
                    }

                    const dayDots = dotsByDay.get(cell.date) ?? [];
                    const isSelected = selected === cell.date;
                    const dayEvents = eventsByDay.get(cell.date) ?? [];
                    const holiday = holidayByDay.get(cell.date);
                    const weeklyOff = isWeeklyOff(cell.date);
                    const record = attendanceByDay.get(cell.date);
                    const isToday = cell.date === todayKey;

                    const flagged = dayDots.includes('flag');
                    const offSite = record?.kind === 'OFF_SITE';
                    const present = Boolean(record) && !offSite && !flagged;

                    // Everything worth a marker on this day. More than two and
                    // the cell shows a count instead — the design's rule.
                    const markerCount =
                      (flagged ? 1 : 0) + (offSite ? 1 : 0) + dayEvents.length;

                    const describe = [
                      flagged && 'flag',
                      offSite && 'off-site',
                      holiday && holiday.name,
                      weeklyOff && 'weekly off',
                      dayEvents.length && `${dayEvents.length} event${dayEvents.length === 1 ? '' : 's'}`,
                    ]
                      .filter(Boolean)
                      .join(', ');

                    return (
                      <button
                        key={cell.key}
                        type="button"
                        onClick={() => setSelected(isSelected ? null : cell.date)}
                        aria-pressed={isSelected}
                        aria-label={`${cell.n} ${monthLabel}${describe ? `, ${describe}` : ''}`}
                        title={holiday ? holiday.name : weeklyOff ? 'Weekly off' : undefined}
                        className="flex h-[52px] flex-col items-center gap-[3px] rounded-[9px] pt-1.5 text-[13px] transition-colors"
                        style={{
                          // Today is the heavy outline; selection is the brand.
                          border: isSelected
                            ? '2px solid var(--brand)'
                            : isToday
                              ? '2px solid var(--ink)'
                              : '1.5px solid var(--rule)',
                          background: isSelected
                            ? 'color-mix(in oklch, var(--brand) 8%, var(--paper))'
                            : holiday
                              ? HATCH
                              : weeklyOff
                                ? 'var(--paper-sunk)'
                                : 'transparent',
                          color: weeklyOff && !isSelected ? 'var(--ink-faint)' : 'var(--ink)',
                          fontWeight: isToday || isSelected ? 700 : 400,
                        }}
                      >
                        {cell.n}

                        {/* One row of shapes: triangle, diamond, dot — or a count. */}
                        <span className="flex h-[15px] items-center justify-center gap-1">
                          {markerCount > 2 ? (
                            <CountPill n={markerCount} />
                          ) : (
                            <>
                              {flagged && <FlagTriangle />}
                              {offSite && <OffsiteDiamond />}
                              {dayEvents.length > 0 && <CountPill n={dayEvents.length} />}
                              {present && !dayEvents.length && <PresentDot />}
                            </>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Legend — every shape the grid can draw, named. */}
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2.5 text-[12.5px] text-ink-soft">
                  <span className="flex items-center gap-1.5">
                    <FlagTriangle /> Flag / complaint
                  </span>
                  <span className="flex items-center gap-1.5">
                    <OffsiteDiamond /> Off-site
                  </span>
                  <span className="flex items-center gap-1.5">
                    <HolidaySwatch /> Festival / holiday
                  </span>
                  <span className="flex items-center gap-1.5">
                    <PresentDot /> On site
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CountPill n={2} /> 2 events
                  </span>
                </div>

                <div className="mt-3 text-[11.5px] text-ink-ghost">
                  {counts.flags} flag{counts.flags === 1 ? '' : 's'} · {counts.offSite} off-site day
                  {counts.offSite === 1 ? '' : 's'} this month
                </div>
              </>
            )}
          </div>

          {/* Selected day detail */}
          {selected && (
            <div className="flex flex-col gap-3 border-t border-rule px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="label-mono text-ink-faint">
                  {new Date(`${selected}T00:00:00Z`)
                    .toLocaleDateString(undefined, {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      timeZone: 'UTC',
                    })
                    .toUpperCase()}
                  {selectedCount > 0 && ` · ${selectedCount} ${selectedCount === 1 ? 'entry' : 'entries'}`}
                </span>

                {isSelf && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 rounded-full px-3 text-[11.5px]"
                    onClick={() => setEventOpen(true)}
                  >
                    <CalendarPlus className="mr-1.5 size-3.5" />
                    Add event
                  </Button>
                )}
              </div>

              {/* Holiday banner */}
              {holidayByDay.get(selected) && (
                <div
                  className="flex items-center gap-2.5 rounded-xl px-3.5 py-2.5"
                  style={{
                    background: 'var(--holiday-bg)',
                    border: '1px solid var(--holiday-border)',
                  }}
                >
                  <PartyPopper className="size-4 flex-none" style={{ color: 'var(--holiday-ink)' }} />
                  <div className="text-[12.5px] font-medium text-ink">
                    {holidayByDay.get(selected)!.name}
                    <span className="ml-1.5 font-normal text-ink-faint">
                      {holidayByDay.get(selected)!.kind === 'OPTIONAL'
                        ? '· restricted holiday'
                        : '· holiday'}
                    </span>
                  </div>
                </div>
              )}

              {!holidayByDay.get(selected) && isWeeklyOff(selected) && (
                <div className="rounded-xl bg-paper-sunk px-3.5 py-2.5 text-[12.5px] text-ink-faint">
                  Weekly off — attendance isn’t expected.
                </div>
              )}

              {/* Events. One tap opens the list; many days have several. */}
              {(eventsByDay.get(selected) ?? []).length > 0 && (
                <button
                  type="button"
                  onClick={() => setViewerOpen(true)}
                  className="flex w-full items-center gap-2.5 rounded-xl border border-rule bg-paper-warm px-3.5 py-2.5 text-left transition-colors hover:border-brand"
                >
                  <Video className="size-4 flex-none text-brand" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12.5px] font-medium text-ink">
                      {(eventsByDay.get(selected) ?? []).length} event
                      {(eventsByDay.get(selected) ?? []).length === 1 ? '' : 's'}
                    </span>
                    <span className="block truncate text-[11.5px] text-ink-faint">
                      {(eventsByDay.get(selected) ?? [])
                        .map((e) => `${e.startTime} ${e.title}`)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="text-[11.5px] font-medium text-brand">Open</span>
                </button>
              )}

              {selectedAttendance && (
                <div
                  className="flex items-start gap-2.5 rounded-[11px] px-3.5 py-3 text-[12.5px] text-ink"
                  style={{
                    border:
                      selectedAttendance.kind === 'OFF_SITE'
                        ? '2px dashed var(--dot-offsite)'
                        : '1.5px solid var(--rule)',
                    background: 'var(--paper)',
                  }}
                >
                  <span className="mt-1 flex-none">
                    {selectedAttendance.kind === 'OFF_SITE' ? <OffsiteDiamond size={11} /> : <PresentDot />}
                  </span>
                  <div className="min-w-0 flex-1">
                  In {formatClock(selectedAttendance.clockInAt)}
                  {selectedAttendance.clockOutAt &&
                    ` → out ${formatClock(selectedAttendance.clockOutAt)}`}{' '}
                  · {formatDuration(selectedAttendance.minutes)}
                  {selectedAttendance.locationName && ` · ${selectedAttendance.locationName}`}
                  {selectedAttendance.kind === 'OFF_SITE' && (
                    <span className="ml-1 text-ink-faint">(off-site)</span>
                  )}
                  {selectedAttendance.signOffNote && (
                    <p className="mt-1.5 text-ink-soft">“{selectedAttendance.signOffNote}”</p>
                  )}
                  </div>
                </div>
              )}

              {selectedRemarks.length === 0 &&
                !selectedAttendance &&
                (eventsByDay.get(selected) ?? []).length === 0 && (
                  <p className="text-[12.5px] text-ink-faint">
                    Nothing on this day yet.
                    {isSelf && ' Add an event to put something here.'}
                  </p>
                )}

              {/* A flag is a card with its own shape, matching the grid marker. */}
              {selectedRemarks.map((remark) => (
                <div
                  key={remark.id}
                  className="flex items-start gap-2.5 rounded-[11px] bg-paper px-3.5 py-3"
                  style={{ border: '2px solid var(--dot-flag)' }}
                >
                  <span className="mt-1 flex-none">
                    <FlagTriangle size={11} />
                  </span>
                  <div className="min-w-0 flex-1 text-[12.5px] leading-relaxed text-ink">
                    {/*
                      An engine-raised flag is stored against the Company Admin
                      because Remark.authorId is required — a storage detail, not
                      a fact about who raised it. Showing that name made an
                      automatic "no clock-in" read as though the admin had
                      personally flagged you. Anything with a `code` came from
                      the rules, so it is attributed to the company.
                    */}
                    <b className="font-semibold">
                      {remark.code
                        ? companyName ?? PRODUCT.name
                        : `${remark.author.title || remark.author.orgRole} · ${remark.author.name}`}
                    </b>{' '}
                    — {remark.body}
                  </div>

                  {/* A flag stands until a Head or the person's own Manager
                      waives it. There is deliberately nothing here the subject
                      can press: "Got it" used to mark the flag ACKNOWLEDGED,
                      which every open-flag count treated as closed — so anyone
                      flagged could clear their own record. */}
                  {remark.status !== 'OPEN' && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-[11px] text-ink-ghost">
                        {remark.status === 'RESOLVED' ? 'waived' : remark.status.toLowerCase()}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <EventDialog
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        dayLabel={
          selected
            ? new Date(`${selected}T00:00:00Z`).toLocaleDateString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                timeZone: 'UTC',
              })
            : ''
        }
        events={selected ? (eventsByDay.get(selected) ?? []) : []}
        onDelete={(id) => void removeEvent(id)}
      />

      {/* Add an event to the selected date */}
      <Dialog open={eventOpen} onOpenChange={setEventOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Add an event
              {selected
                ? ` — ${new Date(`${selected}T00:00:00Z`).toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'long',
                    timeZone: 'UTC',
                  })}`
                : ''}
            </DialogTitle>
            <DialogDescription>
              {eventForm.kind === 'REMINDER'
                ? 'A private note. Nobody else sees it — not your lead, not the Company Admin.'
                : 'Everyone in the company sees this, whoever adds it.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {/* Asked first, because it decides who can read the rest. */}
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  { key: 'EVENT', icon: Users, label: 'Event', hint: 'Everyone sees it' },
                  { key: 'REMINDER', icon: Lock, label: 'Reminder', hint: 'Only you' },
                ] as const
              ).map((option) => {
                const on = eventForm.kind === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setEventForm({ ...eventForm, kind: option.key })}
                    aria-pressed={on}
                    className="flex flex-col items-start gap-0.5 rounded-[12px] px-3.5 py-2.5 text-left transition-colors"
                    style={{
                      border: on ? '2px solid var(--brand)' : '1.5px solid var(--rule)',
                      background: on
                        ? 'color-mix(in oklch, var(--brand) 8%, var(--paper))'
                        : 'var(--paper)',
                    }}
                  >
                    <span className="flex items-center gap-1.5 text-[13.5px] font-semibold text-ink">
                      <option.icon className="size-3.5" strokeWidth={1.9} />
                      {option.label}
                    </span>
                    <span className="text-[11.5px] text-ink-faint">{option.hint}</span>
                  </button>
                );
              })}
            </div>

            <FormField label="What is it?" required>
              <Input
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="Sprint review with the employees"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Starts" required>
                <Input
                  type="time"
                  value={eventForm.startTime}
                  onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })}
                />
              </FormField>
              <FormField label="Ends" helper="Optional">
                <Input
                  type="time"
                  value={eventForm.endTime}
                  onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })}
                />
              </FormField>
            </div>

            <FormField label="Meeting link" helper="Google Meet, Zoom, Teams — optional">
              <Input
                value={eventForm.meetingLink}
                onChange={(e) => setEventForm({ ...eventForm, meetingLink: e.target.value })}
                placeholder="https://meet.google.com/abc-defg-hij"
              />
            </FormField>

            <FormField label="Content" helper="Agenda, notes, or any links you want to share">
              <Textarea
                value={eventForm.notes}
                onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
                rows={4}
                placeholder={'Room 2 · walk through the geofence work\n\nPaste anything here — links become clickable.'}
                className="resize-none"
              />
            </FormField>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEventOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void addEvent()} disabled={!eventForm.title.trim()}>
              Add event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

export default function CalendarRoute() {
  return (
    <AuthGuard>
      <CalendarPage />
    </AuthGuard>
  );
}
